// src/features/auth/signup/components/PhoneVerificationField.tsx

import React from 'react';

import {
  formatPhoneNumber,
  formatVerificationTime,
  normalizePhoneNumber,
} from '../utils/signup.utils';


interface PhoneVerificationFieldProps {
  phoneNumber: string;

  verificationCode: string;

  verificationSent: boolean;

  phoneVerified: boolean;

  isSendingVerification: boolean;

  isVerifyingCode: boolean;

  resendSeconds: number;

  verificationSeconds: number;

  message: string;

  disabled?: boolean;

  inputClassName: string;

  labelClassName: string;

  onPhoneNumberChange: (
    value: string,
  ) => void;

  onVerificationCodeChange: (
    value: string,
  ) => void;

  onSendVerification: () => void;

  onVerifyCode: () => void;
}


export default function PhoneVerificationField({
  phoneNumber,

  verificationCode,

  verificationSent,

  phoneVerified,

  isSendingVerification,

  isVerifyingCode,

  resendSeconds,

  verificationSeconds,

  message,

  disabled = false,

  inputClassName,

  labelClassName,

  onPhoneNumberChange,

  onVerificationCodeChange,

  onSendVerification,

  onVerifyCode,
}: PhoneVerificationFieldProps) {
  /**
   * =========================================================
   * 인증번호 발송 버튼 활성화 여부
   * =========================================================
   */
  const sendDisabled =
    disabled ||
    isSendingVerification ||
    phoneVerified ||
    resendSeconds > 0;


  /**
   * =========================================================
   * 인증번호 확인 버튼 활성화 여부
   * =========================================================
   */
  const verifyDisabled =
    disabled ||
    phoneVerified ||
    isVerifyingCode ||
    verificationSeconds <=
      0 ||
    verificationCode.length !==
      6;


  return (
    <div>

      {/* =====================================================
          전화번호
          ===================================================== */}
      <label
        htmlFor="phoneNumber"
        className={
          labelClassName
        }
      >
        전화번호

        <span className="ml-1 text-red-500">
          *
        </span>

      </label>


      {/* =====================================================
          전화번호 입력 + 인증번호 요청

          Mobile:
          세로

          sm 이상:
          가로
          ===================================================== */}
      <div
        className="
          flex
          flex-col
          gap-2

          sm:flex-row
        "
      >

        <input
          id="phoneNumber"
          type="tel"
          value={
            formatPhoneNumber(
              phoneNumber,
            )
          }
          placeholder="010-0000-0000"
          inputMode="numeric"
          autoComplete="tel"
          maxLength={13}
          disabled={
            disabled ||
            phoneVerified
          }
          onChange={(
            event,
          ) =>
            onPhoneNumberChange(
              normalizePhoneNumber(
                event
                  .target
                  .value,
              ),
            )
          }
          className={`
            ${inputClassName}

            w-full
            min-w-0
          `}
        />


        <button
          type="button"
          disabled={
            sendDisabled
          }
          onClick={
            onSendVerification
          }
          className="
            flex
            min-h-11
            w-full
            shrink-0
            items-center
            justify-center

            rounded-lg

            border
            border-main

            bg-main/5

            px-3

            text-sm
            font-semibold
            text-main

            transition

            hover:bg-main
            hover:text-white

            disabled:cursor-not-allowed
            disabled:opacity-50

            sm:min-h-12
            sm:w-28
          "
        >

          {isSendingVerification
            ? '전송 중'
            : phoneVerified
              ? '인증 완료'
              : resendSeconds >
                  0
                ? `${resendSeconds}초`
                : verificationSent
                  ? '재전송'
                  : '인증번호'}

        </button>

      </div>


      {/* =====================================================
          인증번호 입력
          ===================================================== */}
      {verificationSent && (

        <div className="mt-3">

          <div
            className="
              flex
              flex-col
              gap-2

              sm:flex-row
            "
          >

            {/* =================================================
                인증번호 Input
                ================================================= */}
            <div className="relative min-w-0 flex-1">

              <input
                id="verificationCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={
                  verificationCode
                }
                maxLength={6}
                disabled={
                  disabled ||
                  phoneVerified
                }
                placeholder="인증번호 6자리"
                onChange={(
                  event,
                ) =>
                  onVerificationCodeChange(
                    event
                      .target
                      .value,
                  )
                }
                className={`
                  ${inputClassName}

                  w-full
                  min-w-0
                  pr-20
                `}
              />


              {/* ===============================================
                  인증번호 유효시간

                  pointer-events-none:
                  모바일 터치 이벤트 방해 방지
                  =============================================== */}
              {!phoneVerified &&
                verificationSeconds >
                  0 && (

                  <span
                    className="
                      pointer-events-none

                      absolute
                      right-3
                      top-1/2
                      z-10

                      -translate-y-1/2

                      select-none

                      text-xs
                      font-semibold
                      text-red-500
                    "
                  >

                    {formatVerificationTime(
                      verificationSeconds,
                    )}

                  </span>

                )}

            </div>


            {/* =================================================
                인증 확인
                ================================================= */}
            <button
              type="button"
              disabled={
                verifyDisabled
              }
              onClick={
                onVerifyCode
              }
              className="
                flex
                min-h-11
                w-full
                shrink-0
                items-center
                justify-center

                rounded-lg

                border
                border-gray-300

                bg-gray-100

                px-3

                text-sm
                font-semibold
                text-gray-700

                transition

                hover:border-main
                hover:bg-main/5
                hover:text-main

                disabled:cursor-not-allowed
                disabled:opacity-50

                sm:min-h-12
                sm:w-28
              "
            >

              {isVerifyingCode
                ? '확인 중'
                : phoneVerified
                  ? '인증 완료'
                  : '인증 확인'}

            </button>

          </div>

        </div>

      )}


      {/* =====================================================
          메시지
          ===================================================== */}
      {message && (

        <p
          className={`
            mt-1.5

            text-xs

            sm:text-sm

            ${
              phoneVerified
                ? 'text-emerald-600'
                : 'text-gray-500'
            }
          `}
        >
          {message}
        </p>

      )}

    </div>
  );
}